/**
 * Helpers untuk identitas karyawan (NIK + tanggal lahir)
 * agar lookup konsisten dan tidak membuat dokumen duplikat.
 */

export function normalizeNik(val) {
  return String(val ?? "")
    .trim()
    .replace(/\s+/g, "");
}

export function normalizeTanggalLahir(val) {
  const s = String(val ?? "")
    .trim()
    .replace(/\D/g, "");
  return s.length === 7 ? "0" + s : s;
}

/** Samakan format tanggal 6 digit (DDMMYY) dan 8 digit (DDMMYYYY). */
export function isTanggalLahirMatch(inputVal, dbVal) {
  const a = normalizeTanggalLahir(inputVal);
  const b = normalizeTanggalLahir(dbVal);

  if (!a || !b) return false;
  if (a === b) return true;

  if (a.length === 6 && b.length === 8) {
    return a.slice(0, 4) === b.slice(0, 4) && a.slice(4) === b.slice(6);
  }
  if (a.length === 8 && b.length === 6) {
    return a.slice(0, 4) === b.slice(0, 4) && a.slice(6) === b.slice(4);
  }

  return false;
}

export function nikQueryValues(nik) {
  const nikStr = normalizeNik(nik);
  const values = [nikStr];
  if (/^\d+$/.test(nikStr)) {
    const nikNum = parseInt(nikStr, 10);
    if (!Number.isNaN(nikNum) && !values.includes(nikNum)) {
      values.push(nikNum);
    }
  }
  return values;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Pilih dokumen paling baru dari daftar QueryDocumentSnapshot. */
export function pickLatestEmployeeDoc(docs) {
  if (!docs || docs.length === 0) return null;
  let latest = docs[0];
  let latestDate = Math.max(
    toMillis(latest.data().updated_at),
    toMillis(latest.data().created_at),
  );

  docs.forEach((doc) => {
    const data = doc.data();
    const docDate = Math.max(
      toMillis(data.updated_at),
      toMillis(data.created_at),
    );
    if (docDate > latestDate) {
      latest = doc;
      latestDate = docDate;
    }
  });

  return latest;
}

/**
 * Cari dokumen employees berdasarkan NIK (string/number),
 * opsional filter tanggal lahir yang longgar.
 * Tidak pernah membuat dokumen baru.
 */
export async function findEmployeeDocsByNik(
  collectionRef,
  nik,
  tanggalLahir = null,
) {
  const values = nikQueryValues(nik);
  const seen = new Set();
  const docs = [];

  for (const value of values) {
    const snap = await collectionRef.where("nik", "==", value).get();
    snap.docs.forEach((doc) => {
      if (seen.has(doc.id)) return;
      seen.add(doc.id);
      docs.push(doc);
    });
  }

  if (!tanggalLahir) {
    return docs;
  }

  return docs.filter((doc) =>
    isTanggalLahirMatch(tanggalLahir, doc.data().tanggal_lahir),
  );
}

/**
 * Resolve satu dokumen karyawan untuk update.
 * Prefer docId jika NIK cocok; jika tidak, cari by NIK (+ tanggal).
 */
export async function resolveEmployeeDoc(
  collectionRef,
  { docId = null, nik, tanggalLahir = null } = {},
) {
  const nikStr = normalizeNik(nik);

  if (docId) {
    try {
      const snap = await collectionRef.doc(docId).get();
      if (snap.exists) {
        const data = snap.data() || {};
        if (normalizeNik(data.nik) === nikStr) {
          return { doc: snap, docs: [snap], source: "docId" };
        }
      }
    } catch (error) {
      console.warn("⚠️ Gagal validasi docId:", error);
    }
  }

  let docs = await findEmployeeDocsByNik(
    collectionRef,
    nikStr,
    tanggalLahir,
  );

  // Fallback: kalau filter tanggal kosong, coba NIK saja
  if (docs.length === 0 && tanggalLahir) {
    docs = await findEmployeeDocsByNik(collectionRef, nikStr, null);
  }

  if (docs.length === 0) {
    return { doc: null, docs: [], source: "not_found" };
  }

  const latest = pickLatestEmployeeDoc(docs);
  return {
    doc: latest,
    docs,
    source: docs.length > 1 ? "nik_multiple" : "nik",
  };
}
